import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserCombobox } from "@/features/users/components/UserCombobox";
import { AUDIT_ACTION_OPTIONS } from "@/features/audit-logs/constants/action-labels";
import { Filter, X } from "lucide-react";
import type { Department } from "@/types/department";

interface AuditLogsFiltersProps {
  from: string;
  to: string;
  action?: string;
  userId?: string;
  departmentId?: string;
  departments: Department[];
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onActionChange: (value: string | undefined) => void;
  onUserIdChange: (value: string | undefined) => void;
  onDepartmentChange: (value: string | undefined) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export function AuditLogsFilters({
  from,
  to,
  action,
  userId,
  departmentId,
  departments,
  onFromChange,
  onToChange,
  onActionChange,
  onUserIdChange,
  onDepartmentChange,
  onClearFilters,
  hasActiveFilters,
}: AuditLogsFiltersProps) {
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="size-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">تصفية السجل</h3>
        </div>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            <X className="ml-1 size-4" />
            مسح الفلاتر
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          من تاريخ
          <input
            type="date"
            value={from}
            onChange={(e) => onFromChange(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          إلى تاريخ
          <input
            type="date"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          />
        </label>

        <div className="flex min-w-44 flex-col gap-1">
          <span className="text-xs text-muted-foreground">نوع الإجراء</span>
          <Select
            value={action ?? "all"}
            onValueChange={(v) => onActionChange(v === "all" ? undefined : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="كل الإجراءات" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الإجراءات</SelectItem>
              {AUDIT_ACTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-44 flex-col gap-1">
          <span className="text-xs text-muted-foreground">الدائرة</span>
          <Select
            value={departmentId ?? "all"}
            onValueChange={(v) => onDepartmentChange(v === "all" ? undefined : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="كل الدوائر" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الدوائر</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.nameAr ?? d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-52 flex-col gap-1">
          <span className="text-xs text-muted-foreground">المستخدم</span>
          <div className="flex items-center gap-2">
            <UserCombobox
              value={userId}
              onChange={(id) => onUserIdChange(id || undefined)}
              placeholder="كل المستخدمين"
              className="flex-1"
            />
            {userId && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onUserIdChange(undefined)}
                aria-label="مسح المستخدم"
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {from && <Badge variant="secondary">من: {from}</Badge>}
          {to && <Badge variant="secondary">إلى: {to}</Badge>}
          {action && (
            <Badge variant="secondary">
              {AUDIT_ACTION_OPTIONS.find((o) => o.value === action)?.label ?? action}
            </Badge>
          )}
          {departmentId && (
            <Badge variant="secondary">
              {departments.find((d) => d.id === departmentId)?.nameAr ?? departmentId}
            </Badge>
          )}
          {userId && <Badge variant="secondary">مستخدم محدد</Badge>}
        </div>
      )}
    </div>
  );
}
